import Decimal from 'decimal.js';
import Numeric from '@/core/types/Numeric';
import type { VNode } from 'vue';

/** Цвета сегментов прогресс-бара. Проп называется color: цвет и есть суть сегмента. */
export const PROGRESS_COLORS = ['green', 'blue', 'yellow', 'purple', 'red', 'critical'] as const;

export type ProgressColor = typeof PROGRESS_COLORS[number];

/** Что можно передать в value / current / total: число или семантический тип */
export type ProgressValue = Numeric | number | null | undefined;

/**
 * Сегмент прогресс-бара, разобранный из тега <VProgressBarSegment>.
 *
 * Значения уже приведены к Decimal по правилам бара (см. toDecimal).
 * current — подсвеченная часть сегмента; больше value быть не может:
 * если пришло больше, сегмент расширяется до current.
 */
class ProgressBarSegment {
  readonly color: ProgressColor;

  /** Величина сегмента (с учётом расширения до current) */
  readonly value: Decimal;

  /** Подсвеченная часть сегмента; ноль — подсветки нет */
  readonly current: Decimal;

  /**
   * Исходные значения пропов value и current как пришли — нужны модели,
   * чтобы определить семантический тип для подписей.
   */
  readonly rawValues: ProgressValue[];

  constructor(color: ProgressColor, value: Decimal, current: Decimal, rawValues: ProgressValue[]) {
    this.color = color;
    this.value = value;
    this.current = current;
    this.rawValues = rawValues;
  }

  /** Часть сегмента без подсветки */
  get rest(): Decimal {
    return this.value.minus(this.current);
  }

  get hasCurrent(): boolean {
    return this.current.isZero() === false;
  }

  /** Сегмент из vnode тега <VProgressBarSegment> */
  static fromVNode(vnode: VNode): ProgressBarSegment {
    const props = (vnode.props || {}) as Record<string, unknown>;

    const rawValue = props.value as ProgressValue;
    const rawCurrent = props.current as ProgressValue;

    const value = ProgressBarSegment.toDecimal(rawValue);
    const current = ProgressBarSegment.toDecimal(rawCurrent);

    // Подсвеченная часть не может быть больше сегмента: иначе она и есть сегмент
    if (current.greaterThan(value) === true) {
      console.warn('VProgressBar: current больше value сегмента, сегмент расширен до current', props);
    }

    let segmentValue: Decimal;

    if (current.greaterThan(value) === true) {
      segmentValue = current;
    } else {
      segmentValue = value;
    }

    return new ProgressBarSegment(
      props.color as ProgressColor,
      segmentValue,
      current,
      [rawValue, rawCurrent],
    );
  }

  /**
   * Приводит значение к Decimal. Пустое (null, NaN, Numeric.fromEmpty) — ноль.
   * Отрицательное берётся по модулю: в финансовых данных минус обычно
   * означает направление (возврат, сторно), а не величину.
   * Предупреждение в консоль — чтобы не пропустить минус, пришедший по ошибке.
   *
   * Общее правило бара: им же приводится total в ProgressBarModel.
   */
  static toDecimal(value: ProgressValue): Decimal {
    let result: Decimal;

    if (value === null || value === undefined) {
      result = new Decimal(0);
    } else if (value instanceof Numeric) {
      if (value.isEmpty() === true) {
        result = new Decimal(0);
      } else {
        result = value.value;
      }
    } else if (Number.isFinite(value) === true) {
      result = new Decimal(value);
    } else {
      result = new Decimal(0);
    }

    if (result.lessThan(0) === true) {
      console.warn('VProgressBar: отрицательное значение взято по модулю', value);
      result = result.abs();
    }

    return result;
  }
}

export default ProgressBarSegment;